import * as querystring from 'querystring';
import isViewportAbove from '../utilities/isViewportAbove';
import { createSessionId } from '../utilities/identifiers';
import createPath from './createPath';

interface PathOptions {
  useContentModal?: boolean;
  contentModalBasePath?: string;
}

const getContentModalBasePath = ({ pathname, search }: Location) => {
  return `${pathname}${search}`;
};

const maybeForwardToContentModal = (
  path: string,
  useContentModal: boolean = isViewportAbove('sm'),
  basePath: string = getContentModalBasePath(window.location),
) => {
  if (useContentModal && !window.localStorage.getItem('disableContentModal')) {
    const [pathname, search = ''] = basePath.split('?');
    // Override the existing contentPath if it exists.
    const qs = {
      ...querystring.parse(search),
      contentPath: path,
    };
    return `${pathname}?${querystring.stringify(qs)}`;
  }
  return path;
};

// Base paths

export const home = createPath('/', () => '/');
export const indexHtml = createPath('/index.html', () => '/index.html');

// Login paths

export const login = createPath(
  '/login',
  (queryParams: { backTo?: string } = {}) =>
    `/login?${querystring.stringify({ backTo: queryParams.backTo })}`,
  { unprotected: true },
);

export const loginCallback = createPath(
  '/login-callback',
  () => `/login-callback`,
  { unprotected: true },
);

export const signup = createPath(
  '/signup',
  (queryParams: { backTo?: string } = {}) =>
    `/signup?${querystring.stringify({ backTo: queryParams.backTo })}`,
  { unprotected: true },
);

// TODO: Legacy changePassword redirect, remove after enterprise migration is complete.
export const legacyChangePassword = createPath(
  '/changePassword',
  () => `/changePassword`,
  { unprotected: true },
);

// Screens paths

export const screens = createPath('/screens', () => '/screens');

export const screenSettings = createPath(
  '/screens/:deviceId/settings',
  (deviceId: string) => `/screens/${deviceId}/settings`,
);

export const paymentTerminalSettings = createPath(
  '/screens/:deviceId/payment-terminal',
  (deviceId: string) => `/screens/${deviceId}/payment-terminal`,
);

export const newPaymentTerminal = createPath(
  '/screens/:deviceId/payment-terminal/new',
  (deviceId: string) => `/screens/${deviceId}/payment-terminal/new`,
);

export const deviceShare = createPath(
  '/screens/:deviceId/share',
  (deviceId: string) => `/screens/${deviceId}/share`,
);

export const deviceRename = createPath(
  '/screens/:deviceId/rename',
  (deviceId: string) => `/screens/${deviceId}/rename`,
);

export const screenAiSettings = createPath(
  '/screens/:deviceId/ai/settings',
  (deviceId: string) => `/screens/${deviceId}/ai/settings`,
);

export const screenPerchSettings = createPath(
  '/screens/:deviceId/perch/settings',
  (deviceId: string) => `/screens/${deviceId}/perch/settings`,
);

export const screenPlaylist = createPath(
  '/screens/:deviceId/playlist',
  (deviceId: string) => `/screens/${deviceId}/playlist`,
);

// Library paths
export const library = createPath('/library', () => `/library`);

export const libraryFolder = createPath(
  '/library/folders/:folderId',
  (folderId: string) => `/library/folders/${folderId}`,
);

export const libraryProfile = createPath(
  '/library/profiles/:profileId',
  (profileId: string) => `/library/profiles/${profileId}`,
);

export const librarySearch = createPath(
  '/library/search',
  () => `/library/search`,
);

export const librarySharedWithMe = createPath(
  '/library/shared',
  () => `/library/shared`,
);

export const librarySharedLibraries = createPath(
  '/library/shared-libraries',
  () => `/library/shared-libraries`,
);

export const libraryUsersLibraries = createPath(
  '/library/users',
  () => `/library/users`,
);

// "See in Library" — navigate to a content item's location in the library and
// flag it for highlight. `folderId` omitted (null/empty) lands at the tab root.
export interface SeeInLibraryQueryParams {
  folderId?: string | null;
  highlightResourceId: string;
  // URL to return to when the user backs out from the landing folder (the
  // playlist editor they came from). Paired with `backToFolder`, the folder we
  // land in, which marks the back-to-playlist boundary in the library.
  backTo?: string;
  // Omit to default to `folderId` (the landing folder); pass null explicitly
  // for content that really sits at the library root.
  backToFolder?: string | null;
}
export const seeInLibrary = createPath(
  '/library',
  ({
    folderId,
    highlightResourceId,
    backTo,
    backToFolder,
  }: SeeInLibraryQueryParams) => {
    const search = new URLSearchParams();
    if (folderId) search.set('folder_id', folderId);
    search.set('highlight', highlightResourceId);
    if (backTo) {
      search.set('back_to', backTo);
      // Omitted backToFolder falls back to the landing folder so a caller
      // can't accidentally mark the library root as the boundary. Empty string
      // is still a valid boundary value (content at the library root), so only
      // the presence of back_to gates this — set it even when empty.
      const boundary = backToFolder === undefined ? folderId : backToFolder;
      search.set('back_to_folder', boundary ?? '');
    }
    return `/library?${search.toString()}`;
  },
);

export const gettingStarted = createPath(
  '/getting-started',
  () => `/getting-started`,
);

export const plans = createPath(
  '/plans/:productIdentifier',
  (productIdentifier: string = 'pro') => `/plans/${productIdentifier}`,
);

// Presentation paths
// TODO: Since nested playlists this page can also select playlists. A better name or it would
// be `add-content`but this page will eventually be removed in favour of selecting content
// with an inline library on the builder page.

export interface PresentationsQueryParams {
  folderId?: string;
  profileId?: string;
  selectionId?: string;
  playlistId?: string;
  backTo: string;
  saveTo: string;
  addToLabel: string;
  sessionId?: string;
  createPlaylist?: boolean;
  /** If provided, items will be added to this playlist directly (used for nested playlists) */
  addToPlaylistId?: string;
  /** Single-select mode – only one item can be selected at a time */
  singleSelect?: boolean;
  /** Only show playlists in the library grid */
  playlistOnly?: boolean;
  /** Content ID to add to the selected playlist (used with singleSelect + playlistOnly) */
  addContentId?: string;
  /** Content type of the item to add ('presentation' | 'playlist') */
  addContentType?: string;
  /** Library scope to open by default ('shared_library', etc.) */
  scope?: string;
}
export const presentations = createPath(
  '/presentations',
  (queryParams: PresentationsQueryParams, opts: PathOptions = {}) =>
    maybeForwardToContentModal(
      `/presentations?${querystring.stringify({
        folderId: queryParams.folderId,
        profileId: queryParams.profileId,
        selectionId: queryParams.selectionId,
        playlistId: queryParams.playlistId,
        backTo: queryParams.backTo,
        saveTo: queryParams.saveTo,
        addToLabel: queryParams.addToLabel,
        sessionId: queryParams.sessionId,
        createPlaylist: queryParams.createPlaylist,
        addToPlaylistId: queryParams.addToPlaylistId,
        singleSelect: queryParams.singleSelect,
        playlistOnly: queryParams.playlistOnly,
        addContentId: queryParams.addContentId,
        addContentType: queryParams.addContentType,
        scope: queryParams.scope,
      })}`,
      opts.useContentModal,
      opts.contentModalBasePath,
    ),
);

export interface EditPresentationQueryParams {
  backTo: string;
  backToLabel: string;
  saveTo: string;
  previewMode?: string;
  previewOrientation?: string;
  previewResolution?: string;
  selectionId?: string;
  didSave?: boolean;
  sessionId?: string;
}

export const editPresentation = createPath(
  '/presentations/:presentationId',
  (presentationId: string, queryParams: EditPresentationQueryParams) =>
    `/presentations/${presentationId}?${querystring.stringify({
      backTo: queryParams.backTo,
      backToLabel: queryParams.backToLabel,
      saveTo: queryParams.saveTo,
      previewMode: queryParams.previewMode,
      previewOrientation: queryParams.previewOrientation,
      previewResolution: queryParams.previewResolution,
      selectionId: queryParams.selectionId,
      didSave: queryParams.didSave,
      sessionId: queryParams.sessionId,
    })}`,
);

export const editPresentationEditTheme = createPath(
  '/presentations/:presentationId/themes/:themeId',
  (
    presentationId: string,
    themeId: string,
    queryParams: EditPresentationQueryParams,
  ) =>
    `/presentations/${presentationId}/themes/${themeId}?${querystring.stringify(
      {
        backTo: queryParams.backTo,
        backToLabel: queryParams.backToLabel,
        saveTo: queryParams.saveTo,
        previewMode: queryParams.previewMode,
        previewOrientation: queryParams.previewOrientation,
        previewResolution: queryParams.previewResolution,
        selectionId: queryParams.selectionId,
        didSave: queryParams.didSave,
        sessionId: queryParams.sessionId,
      },
    )}`,
);

export const editPresentationNewTheme = createPath(
  '/presentations/:presentationId/themes/new',
  (presentationId: string, queryParams: EditPresentationQueryParams) =>
    `/presentations/${presentationId}/themes/new?${querystring.stringify({
      backTo: queryParams.backTo,
      backToLabel: queryParams.backToLabel,
      saveTo: queryParams.saveTo,
      previewMode: queryParams.previewMode,
      previewOrientation: queryParams.previewOrientation,
      previewResolution: queryParams.previewResolution,
      selectionId: queryParams.selectionId,
      didSave: queryParams.didSave,
      sessionId: queryParams.sessionId,
    })}`,
);

export const editPresentationManageThemes = createPath(
  '/presentations/:presentationId/themes',
  (presentationId: string, queryParams: EditPresentationQueryParams) =>
    `/presentations/${presentationId}/themes?${querystring.stringify({
      backTo: queryParams.backTo,
      backToLabel: queryParams.backToLabel,
      saveTo: queryParams.saveTo,
      previewMode: queryParams.previewMode,
      previewOrientation: queryParams.previewOrientation,
      previewResolution: queryParams.previewResolution,
      selectionId: queryParams.selectionId,
      didSave: queryParams.didSave,
      sessionId: queryParams.sessionId,
    })}`,
);

export interface NewPresentationQueryParams {
  applicationId: string;
  backTo: string;
  backToLabel: string;
  saveTo: string;
  previewMode?: string;
  previewOrientation?: string;
  previewResolution?: string;
  selectionId?: string;
  folderId?: string;
  didSave?: boolean;
  sessionId?: string;
  version?: string;
}

export const newPresentation = createPath(
  '/presentations/new',
  (queryParams: NewPresentationQueryParams) =>
    `/presentations/new?${querystring.stringify({
      applicationId: queryParams.applicationId,
      backTo: queryParams.backTo,
      backToLabel: queryParams.backToLabel,
      saveTo: queryParams.saveTo,
      previewMode: queryParams.previewMode,
      previewOrientation: queryParams.previewOrientation,
      previewResolution: queryParams.previewResolution,
      selectionId: queryParams.selectionId,
      folderId: queryParams.folderId,
      didSave: queryParams.didSave,
      sessionId: queryParams.sessionId,
      version: queryParams.version,
    })}`,
);

export const newPresentationEditTheme = createPath(
  '/presentations/new/themes/:themeId',
  (themeId: string, queryParams: NewPresentationQueryParams) =>
    `/presentations/new/themes/${themeId}?${querystring.stringify({
      applicationId: queryParams.applicationId,
      backTo: queryParams.backTo,
      backToLabel: queryParams.backToLabel,
      saveTo: queryParams.saveTo,
      previewMode: queryParams.previewMode,
      previewOrientation: queryParams.previewOrientation,
      previewResolution: queryParams.previewResolution,
      selectionId: queryParams.selectionId,
      folderId: queryParams.folderId,
      didSave: queryParams.didSave,
      sessionId: queryParams.sessionId,
      version: queryParams.version,
    })}`,
);

export const newPresentationNewTheme = createPath(
  '/presentations/new/themes/new',
  (queryParams: NewPresentationQueryParams) =>
    `/presentations/new/themes/new?${querystring.stringify({
      applicationId: queryParams.applicationId,
      backTo: queryParams.backTo,
      backToLabel: queryParams.backToLabel,
      saveTo: queryParams.saveTo,
      previewMode: queryParams.previewMode,
      previewOrientation: queryParams.previewOrientation,
      previewResolution: queryParams.previewResolution,
      selectionId: queryParams.selectionId,
      folderId: queryParams.folderId,
      didSave: queryParams.didSave,
      sessionId: queryParams.sessionId,
      version: queryParams.version,
    })}`,
);

export const newPresentationManageThemes = createPath(
  '/presentations/new/themes',
  (queryParams: NewPresentationQueryParams) =>
    `/presentations/new/themes?${querystring.stringify({
      applicationId: queryParams.applicationId,
      backTo: queryParams.backTo,
      backToLabel: queryParams.backToLabel,
      saveTo: queryParams.saveTo,
      previewMode: queryParams.previewMode,
      previewOrientation: queryParams.previewOrientation,
      previewResolution: queryParams.previewResolution,
      selectionId: queryParams.selectionId,
      folderId: queryParams.folderId,
      didSave: queryParams.didSave,
      sessionId: queryParams.sessionId,
      version: queryParams.version,
    })}`,
);

// Application paths

export interface ApplicationsQueryParams {
  backTo: string;
  selectionId?: string;
  folderId?: string;
  sessionId?: string;
  createPlaylistTo?: string;
}
export const applications = createPath(
  '/applications',
  (queryParams: ApplicationsQueryParams, opts: PathOptions = {}) =>
    maybeForwardToContentModal(
      `/applications?${querystring.stringify({
        backTo: queryParams.backTo,
        selectionId: queryParams.selectionId,
        folderId: queryParams.folderId,
        sessionId: queryParams.sessionId,
        createPlaylistTo: queryParams.createPlaylistTo,
      })}`,
      opts.useContentModal,
      opts.contentModalBasePath,
    ),
);

export const application = createPath(
  '/applications/:applicationId',
  (applicationId: string) => `/apps/${applicationId}`,
);

// Account paths

export const account = createPath('/account', () => `/account`);

export const accountDomainMembers = createPath(
  '/account/domain-members/:profileId',
  (profileId: string) => `/account/domain-members/${profileId}`,
);

// Unsubscribe paths

export interface UnsubscribeQueryParams {
  userId: string;
}
export const unsubscribe = createPath(
  '/unsubscribe',
  (queryParams: UnsubscribeQueryParams) =>
    `/unsubscribe?${querystring.stringify({ userId: queryParams.userId })}`,
  { unprotected: true },
);

// Playlist paths

export interface PlaylistQueryParams {
  backTo: string;
  backToLabel: string;
  saveTo: string;
  previewMode?: string;
  didSave?: boolean;
  deviceId?: string;
  folderId?: string;
  sessionId?: string;
  addItemsAfterPath?: string;
  /** If true, items will be added inside the playlist at addItemsAfterPath instead of after it */
  addToPlaylist?: boolean;
  /** If provided, items will be added to this playlist directly (used when adding to nested playlists) */
  addToPlaylistId?: string;
  /** Library scope context (e.g. 'shared_library') – propagated so add-content modal opens the right library */
  scope?: string;
}

export interface PlaylistScheduleQueryParams extends PlaylistQueryParams {
  cancelTo: string;
}

export interface EditPlaylistQueryParams extends PlaylistQueryParams {}

export const editPlaylist = createPath(
  '/playlists/:playlistId',
  (playlistId: string, queryParams: PlaylistQueryParams) =>
    `/playlists/${playlistId}?${querystring.stringify({
      // TODO: Make sessionId required?
      sessionId: queryParams.sessionId || createSessionId(),
      deviceId: queryParams.deviceId,
      folderId: queryParams.folderId,
      previewMode: queryParams.previewMode,
      backToLabel: queryParams.backToLabel,
      backTo: queryParams.backTo,
      saveTo: queryParams.saveTo,
      addItemsAfterPath: queryParams.addItemsAfterPath,
      addToPlaylist: queryParams.addToPlaylist,
      addToPlaylistId: queryParams.addToPlaylistId,
      scope: queryParams.scope,
      // TODO: I don't think we need this anymore
      didSave: queryParams.didSave,
    })}`,
);

// TODO: This path is unused when nested playlists is enabled for all users.
export const editPlaylistSchedule = createPath(
  '/playlists/:playlistId/schedule',
  (playlistId: string, queryParams: PlaylistQueryParams) =>
    `/playlists/${playlistId}/schedule?${querystring.stringify({
      backTo: queryParams.backTo,
      backToLabel: queryParams.backToLabel,
      saveTo: queryParams.saveTo,
      previewMode: queryParams.previewMode,
      didSave: queryParams.didSave,
      deviceId: queryParams.deviceId,
      folderId: queryParams.folderId,
      sessionId: queryParams.sessionId,
    })}`,
);

// TODO: This path is unused when nested playlists is enabled for all users.
export const editPlaylistEditSchedule = createPath(
  '/playlists/:parentPlaylistId/schedule/:playlistId',
  (
    parentPlaylistId: string,
    playlistId: string,
    queryParams: PlaylistScheduleQueryParams,
  ) =>
    `/playlists/${parentPlaylistId}/schedule/${playlistId}?${querystring.stringify(
      {
        backTo: queryParams.backTo,
        backToLabel: queryParams.backToLabel,
        saveTo: queryParams.saveTo,
        previewMode: queryParams.previewMode,
        didSave: queryParams.didSave,
        cancelTo: queryParams.cancelTo,
        deviceId: queryParams.deviceId,
        folderId: queryParams.folderId,
      },
    )}`,
);

// TODO: This path is unused when nested playlists is enabled for all users.
export const editPlaylistNewSchedule = createPath(
  '/playlists/:parentPlaylistId/schedule/new',
  (parentPlaylistId: string, queryParams: PlaylistScheduleQueryParams) =>
    `/playlists/${parentPlaylistId}/schedule/new?${querystring.stringify({
      backTo: queryParams.backTo,
      backToLabel: queryParams.backToLabel,
      saveTo: queryParams.saveTo,
      previewMode: queryParams.previewMode,
      cancelTo: queryParams.cancelTo,
      deviceId: queryParams.deviceId,
      folderId: queryParams.folderId,
    })}`,
);

export interface NewPlaylistQueryParams extends PlaylistQueryParams {
  playlistId: string;
  playlistName?: string;
}

export const newPlaylist = createPath(
  '/playlists/new',
  (queryParams: NewPlaylistQueryParams) =>
    `/playlists/new?${querystring.stringify({
      playlistId: queryParams.playlistId,
      playlistName: queryParams.playlistName,
      sessionId: queryParams.sessionId || createSessionId(),
      deviceId: queryParams.deviceId,
      folderId: queryParams.folderId,
      previewMode: queryParams.previewMode,
      backTo: queryParams.backTo,
      backToLabel: queryParams.backToLabel,
      saveTo: queryParams.saveTo,
      addItemsAfterPath: queryParams.addItemsAfterPath,
      addToPlaylist: queryParams.addToPlaylist,
      addToPlaylistId: queryParams.addToPlaylistId,
      scope: queryParams.scope,
    })}`,
);

// TODO: This path is unused when nested playlists is enabled for all users.
export const newPlaylistSchedule = createPath(
  '/playlists/new/schedule',
  (queryParams: NewPlaylistQueryParams) =>
    `/playlists/new/schedule?${querystring.stringify({
      backTo: queryParams.backTo,
      backToLabel: queryParams.backToLabel,
      saveTo: queryParams.saveTo,
      previewMode: queryParams.previewMode,
      deviceId: queryParams.deviceId,
      folderId: queryParams.folderId,
    })}`,
);

// TODO: This path is unused when nested playlists is enabled for all users.
export const newPlaylistEditSchedule = createPath(
  '/playlists/new/schedule/:playlistId',
  (playlistId: string, queryParams: PlaylistScheduleQueryParams) =>
    `/playlists/new/schedule/${playlistId}?${querystring.stringify({
      backTo: queryParams.backTo,
      backToLabel: queryParams.backToLabel,
      saveTo: queryParams.saveTo,
      didSave: queryParams.didSave,
      previewMode: queryParams.previewMode,
      cancelTo: queryParams.cancelTo,
      deviceId: queryParams.deviceId,
      folderId: queryParams.folderId,
    })}`,
);

// TODO: This path is unused when nested playlists is enabled for all users.
export const newPlaylistNewSchedule = createPath(
  '/playlists/new/schedule/new',
  (queryParams: PlaylistScheduleQueryParams) =>
    `/playlists/new/schedule/new?${querystring.stringify({
      backTo: queryParams.backTo,
      backToLabel: queryParams.backToLabel,
      saveTo: queryParams.saveTo,
      previewMode: queryParams.previewMode,
      cancelTo: queryParams.cancelTo,
      deviceId: queryParams.deviceId,
      folderId: queryParams.folderId,
    })}`,
);

// Enterprise

export interface EnterpriseWelcomeQueryParms {
  domainName: string;
}

export const enterpriseWelcome = createPath(
  '/enterprise-welcome',
  (queryParams: EnterpriseWelcomeQueryParms) =>
    `/enterprise-welcome?domain_name=${queryParams.domainName}`,
  { unprotected: true },
);

// Activate

export const activate = createPath(
  '/activate/:code',
  (code: string) => `/activate/${code}`,
);

// Welcome Page
export const welcome = createPath('/welcome', () => `/welcome`);

// Analytics

export const analytics = createPath('/analytics', () => `/analytics`);
export const aiAnalytics = createPath('/ai-analytics', () => `/ai-analytics`);
export const contentEngagement = createPath(
  '/analytics/content',
  () => `/analytics/content`,
);

export const trafficAwareness = createPath(
  '/analytics/traffic',
  () => `/analytics/traffic`,
);

export const contentDetail = createPath(
  '/analytics/content/detail',
  () => `/analytics/content/detail`,
);

export const integration = createPath('/integration', () => '/integration');
export const createIntegration = createPath(
  '/integration/create',
  () => '/integration/create',
);
export const integrationSettings = createPath(
  '/integration/:integrationId/settings',
  (integrationId: string) => `/integration/${integrationId}/settings`,
);

// Marketplace
export const marketplace = createPath('/marketplace', () => `/marketplace`);
export const marketplaceV2 = createPath(
  // will be replaced to /marketplace once all the requirements are done
  '/marketplace/v2',
  () => `/marketplace/v2`,
);

// Integrations + Catalogs
export const catalogs = createPath('/catalogs', () => `/catalogs`);
export const catalogDetails = createPath(
  '/catalogs/:catalogId',
  (catalogId: string) => `/catalogs/${catalogId}`,
);
export const integrations = createPath('/integrations', () => `/integrations`);
export const createIntegrations = createPath(
  '/integrations/create',
  () => `/integrations/create`,
);
export const editIntegrations = createPath(
  '/integrations/:integrationId/edit',
  (integrationId: string) => `/integrations/${integrationId}/edit`,
);
export const createCatalog = createPath(
  '/catalogs/create',
  () => `/catalogs/create`,
);
export const editCatalog = createPath(
  '/catalogs/:catalogId/edit',
  (catalogId: string) => `/catalogs/${catalogId}/edit`,
);
